import type { GraphQLErrorPayload as GraphQLErrorInterop } from '@confluence/relay-errors/entry-points/RelayError.ts';

import { expValEquals } from '@confluence/feature-experiments';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';
import { getLogger } from '@confluence/logger';
import { preloadEditorPageCoverPicture } from '@confluence/page-cover-picture/entry-points/preloadEditorPageCoverPicture.ts';
import { preloadEmojiTitle } from '@confluence/emoji-title/entry-points/preloadEmojiTitle.ts';
import { preloadEditorContentState } from '@confluence/content-state/entry-points/preloadEditorContentState.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import { preloadDraftContentAppearance } from '@confluence/content-appearance/entry-points/preloadDraftContentAppearance.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { preloadContentMode } from '@confluence/editor-content-mode-button/entry-points/preloadContentMode.ts';
/* eslint-disable-next-line import/no-extraneous-dependencies */
import { preloadLastUpdated } from '@confluence/content-header/entry-points/preloadLastUpdated.ts';
import { preloadEditorPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadEditorPageTitleContentProperties.ts';
import { preloadMentionsProvider } from '@confluence/fabric-media-support/entry-points/preloadMentions.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime.ts';
import { preloadBylinePageAdf } from '@confluence/byline-connections/entry-points/preloadBylinePageAdf.ts';
import { SYSTEM_CONTENT_BYLINE } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { isCollabDraftCacheStale } from '@confluence/fabric-providers/entry-points/CollabDraftCacheManager.ts';
import { preloadMacroDataFromCollabDraft } from '@confluence/preload-macros/entry-points/preloadMacroData.ts';
import type { EditorContentStateQueryType } from '@confluence/content-state/entry-points/EditorContentStateQuery.ts';

import { preloadCollabDraftContent, preloadNativeCollab } from './preloadNativeEditor.ts';
import { preloadEditorNodeDataProvidersData } from './preloadEditorNodeDataProvidersData.ts';
import { preloadRovoChatMentionIdentity } from './preloadRovoChatMentionIdentity.ts';
import type { IPreloadEditorData } from './types.ts';

const logger = getLogger('load-edit-page');

export function preloadEditorDataContentState({
	contentId,
	useCache = false,
	isLivePage = false,
}: Pick<IPreloadEditorData, 'contentId' | 'useCache' | 'isLivePage'>): Promise<{
	data: EditorContentStateQueryType | null;
	errors?: ReadonlyArray<GraphQLErrorInterop>;
	hasErrors: boolean;
}> {
	return preloadEditorContentState({
		contentId,
		useCache,
		shouldGetDraftVersion: !isLivePage,
	});
}

/**
 * For preloading the editor of a draft.
 * Live Pages will have its own editor preloading.
 */
export async function preloadEditorData({
	spaceKey,
	contentId,
	shouldPreloadMentionsProvider = false,
	useCache = false, // use cache for native editor data
	catchFn,
	isBlog,
	cloudId,
	isLivePage = false,
	isSsrRoutePreload = false,
	contentStatePreloadPromise,
}: IPreloadEditorData): Promise<void> {
	// note: explicitly casting to Promise<any> to avoid typescript incorrectly inferring tuple types; although we don't use the return value of this function regardless
	logger.debug`Calling preloadEditorData ${performance.now() / 1000}`;
	const draftShareId = getDraftShareIdFromQueryParam();
	const shouldReuseContentStatePreloadPromise = contentStatePreloadPromise !== undefined;
	// preload queries shared by Synchrony and Native Collab editors
	// Under the experiment EditorContentUnifiedQuery, preloaded with the route, serves the title group:
	// content appearance, emoji title, cover picture and title content properties.
	const immediateResultsPromises: Array<Promise<any> | undefined> = expValEquals(
		'confluence_editor_content_unified_query',
		'isEnabled',
		true,
	)
		? []
		: [preloadDraftContentAppearance(contentId, useCache)];

	// specific native collab editor queries
	if (!!contentId) {
		const collabDraftPromise = preloadCollabDraftContent(
			contentId,
			draftShareId,
			useCache || !isCollabDraftCacheStale(contentId, draftShareId),
		);
		immediateResultsPromises.push(collabDraftPromise);

		immediateResultsPromises.push(
			preloadMacroDataFromCollabDraft({
				collabDraftPromise,
				context: { contentId, spaceKey: spaceKey || '' },
				route: isLivePage ? 'live' : 'edit',
			}),
		);

		immediateResultsPromises.push(preloadEditorNodeDataProvidersData(contentId, cloudId, isBlog));

		const { isArchived } = getCachedPageData(contentId ?? undefined);
		if (!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)) {
			immediateResultsPromises.push(
				preloadContentMode({ contentId, isDraft: !isLivePage, useCache, isArchived }),
			);
		}

		if (!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)) {
			immediateResultsPromises.push(preloadEmojiTitle(contentId, draftShareId, useCache));
		}
		immediateResultsPromises.push(preloadNativeCollab(contentId, draftShareId, useCache));
	}

	if (!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)) {
		immediateResultsPromises.push(preloadEditorPageCoverPicture(contentId, draftShareId, useCache));
	}
	immediateResultsPromises.push(
		shouldReuseContentStatePreloadPromise
			? contentStatePreloadPromise
			: preloadEditorDataContentState({
					contentId,
					useCache,
					isLivePage,
				}),
	);

	if (isLivePage) {
		if (
			!(
				isSsrRoutePreload &&
				isSSRStreaming() &&
				expValEquals('confluence_ssr_byline', 'isEnabled', true)
			)
		) {
			// Pre-loading apps in the byline area for live pages. Currently only live pages have extension point apps and not classic editor.
			immediateResultsPromises.push(
				preloadWebItemLocation({ contentId, location: SYSTEM_CONTENT_BYLINE }),
				// For some reason the addition of the filter (uses new Forge query) is causing SSR to timeout - workaround by skipping the filter
				preloadUseExtensionList({
					spaceKey: spaceKey || '',
					moduleType: FORGE_MODULE_BYLINE,
				}),
			);
		}

		immediateResultsPromises.push(preloadLastUpdated(contentId, useCache));

		immediateResultsPromises.push(preloadReadTime({ contentId, useCache }));
		immediateResultsPromises.push(preloadBylinePageAdf({ contentId, useCache }));
	}

	if (shouldPreloadMentionsProvider) {
		immediateResultsPromises.push(preloadMentionsProvider());
	}
	if (expValEquals('platform_editor_mention_rovo', 'isEnabled', true)) {
		immediateResultsPromises.push(preloadRovoChatMentionIdentity(cloudId));
	}

	if (!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)) {
		immediateResultsPromises.push(
			preloadEditorPageTitleContentProperties({
				contentId,
				draftShareId,
				useCache,
			}),
		);
	}

	await waitForAllReturnWhenError(
		immediateResultsPromises.map((p) => (!!catchFn ? p?.catch(catchFn) : p)),
	);

	logger.debug`preloadEditorData complete ${performance.now() / 1000}`;
}
